"use client";

import { useField } from "formik";
import Select, {
	type GroupBase,
	type Props as ReactSelectProps,
	type SingleValue,
	type StylesConfig,
} from "react-select";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
export interface SelectOption {
	value: string;
	label: string;
}

export type SelectGroup = GroupBase<SelectOption>;

type BaseReactSelectProps = ReactSelectProps<SelectOption>;

export interface SelectFieldProps extends Omit<
	BaseReactSelectProps,
	"options" | "value" | "onChange" | "name"
> {
	label: string;
	name: string;
	options: SelectGroup[];
	placeholder?: string;
	helperText?: string;
	className?: string;
	onValueChange?: (value: string, option: SelectOption | null) => void;
}

const baseSelectStyles: StylesConfig = {
	control: (base: any, state: any) => ({
		...base,
		borderRadius: "var(--radius-field)",
		borderWidth: 1,
		transition: "border-color 0.2s, box-shadow 0.2s",
		borderColor: "var(--border)!important",
	}),
	menu: (base: any) => ({
		...base,
		backgroundColor: "white",
		borderRadius: "var(--radius-field)",
		boxShadow:
			"0px 20px 25px -5px rgba(0,0,0,0.1), 0px 10px 10px -5px rgba(0,0,0,0.04)",
		overflow: "hidden",
		marginTop: 8,
	}),
	menuList: (base: any) => ({
		...base,
		padding: 0,
	}),
	groupHeading: (base: any) => ({
		...base,
		fontWeight: 600,
		fontSize: "0.75rem",
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: "oklch(var(--bc) / 0.6)",
		padding: "0.75rem 1rem 0.25rem",
	}),
	option: (base: any, state: { isSelected: any; isFocused: any }) => ({
		...base,
		padding: "0.75rem 1rem",
		backgroundColor: state.isSelected
			? "oklch(var(--p) / 1)"
			: state.isFocused
				? "oklch(var(--p) / 0.15)"
				: "transparent",
		"&:hover": {
			backgroundColor: "var(--color-extended-blue-gray-100)",
		},
		color: state.isSelected
			? "oklch(var(--pc) / 1)"
			: "var(--fallback-bc, oklch(var(--bc)/1))",
		cursor: "pointer",
	}),
	placeholder: (base: any) => ({
		...base,
		color: "oklch(var(--bc) / 0.5)",
	}),
	singleValue: (base: any) => ({
		...base,
		color: "var(--fallback-bc, oklch(var(--bc)/1))",
	}),
	input: (base: any) => ({
		...base,
		color: "var(--fallback-bc, oklch(var(--bc)/1))",
	}),
	indicatorSeparator: () => ({
		display: "none",
	}),
	dropdownIndicator: (base: any, state: { isFocused: any }) => ({
		...base,
		color: state.isFocused
			? "oklch(var(--p) / 1)"
			: "oklch(var(--bc) / 0.6)",
	}),
};

const createSelectStyles = (hasError: boolean): StylesConfig => ({
	...baseSelectStyles,
	control: (base: any, state: { isFocused: any }) => {
		const controlFn = baseSelectStyles.control;
		const baseStyles =
			typeof controlFn === "function" ? controlFn(base, state) : base;
		return {
			...baseStyles,
			borderColor: hasError
				? "oklch(var(--primary) / 1)"
				: state.isFocused
					? "var(--foreground)!important"
					: "var(--border)!important",
			outline: state.isFocused ? "2px solid var(--foreground)" : "none",
			outlineOffset: "2px",
			boxShadow: state.isFocused
				? "0 0 0 1px oklch(var(--p) / 0.3)"
				: "none",
		};
	},
});

export function SelectField({
	label,
	className,
	options,
	placeholder,
	helperText,
	name,
	onValueChange,
	...selectProps
}: SelectFieldProps) {
	const [field, meta, helpers] = useField(name);
	const hasError = Boolean(meta.touched && meta.error);
	const { onBlur, instanceId, inputId, menuPosition, ...restSelectProps } =
		selectProps as {
			onBlur?: (event: any) => void;
			instanceId?: string;
			inputId?: string;
			menuPosition?: "absolute" | "fixed";
		};
	const flatOptions = useMemo(
		() => options.flatMap((group) => group.options ?? []),
		[options],
	);

	const selectedOption = useMemo(
		() =>
			flatOptions.find((option) => option.value === field.value) ?? null,
		[field.value, flatOptions],
	);

	return (
		<label className={cn("form-control w-full", className)}>
			<div className="label">
				<span className="label-text font-semibold">{label}</span>
				{helperText ? (
					<span className="label-text-alt text-sm text-muted-foreground">
						{helperText}
					</span>
				) : null}
			</div>
			<Select
				{...restSelectProps}
				instanceId={instanceId ?? name}
				inputId={inputId ?? name}
				menuPosition={menuPosition ?? "absolute"}
				name={name}
				options={options}
				placeholder={placeholder}
				value={selectedOption}
				onChange={(option) => {
					const singleOption = option as SingleValue<SelectOption>;
					const value = singleOption?.value ?? "";
					helpers.setValue(value);
					onValueChange?.(value, singleOption ?? null);
				}}
				onBlur={(event) => {
					helpers.setTouched(true);
					if (typeof onBlur === "function") {
						onBlur(event);
					}
				}}
				styles={createSelectStyles(hasError)}
				classNamePrefix="contact-select"
				menuShouldScrollIntoView
			/>
			{hasError ? (
				<div className="label">
					<span className="label-text-alt text-error">
						{meta.error}
					</span>
				</div>
			) : null}
		</label>
	);
}
